import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ChevronLeft, RefreshCw } from 'lucide-react';

import AddManufacturerModal from '../../components/catalogue/AddManufacturerModal';
import AddProductModal from '../../components/catalogue/AddProductModal';
import AddSeriesModal from '../../components/catalogue/AddSeriesModal';
import CatalogueManufacturerSidebar from '../../components/catalogue/CatalogueManufacturerSidebar';
import CatalogueProductSidebar from '../../components/catalogue/CatalogueProductSidebar';
import CatalogueSeriesSidebar from '../../components/catalogue/CatalogueSeriesSidebar';
import ManufacturerGrid from '../../components/catalogue/ManufacturerGrid';
import ProductDetail from '../../components/catalogue/ProductDetail';
import ProductGridView from '../../components/catalogue/ProductGridView';
import Footer from '../../components/projects/Footer';
import Header from '../../components/projects/Header';
import { useDashboardTabNavigation } from '../../components/projects/useDashboardTabNavigation';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { catalogueReducer } from '../../store/catalogue/catalogue.reducer';
import {
  getCatalogueIsLoading,
  getCatalogueIsLoadingProductDetail,
  getCatalogueManufacturers,
  getCatalogueSelectedVariantId,
  getSelectedManufacturer,
  getSelectedProduct,
  getSelectedSeries,
} from '../../store/catalogue/catalogue.selectors';
import { getUser } from '../../store/selectors/user.selector';

const {
  fetchManufacturers,
  selectManufacturer,
  selectSeries,
  selectProduct,
  selectVariant,
  createManufacturer,
  createProduct,
  syncCatalogue,
} = catalogueReducer.actions;

/** Roles that grant full read+write access to the catalogue. */
const CATALOGUE_WRITE_ROLES = ['ADMIN', 'CATALOGUE_MANAGER'];

interface CatalogueContentProps {
  canEdit: boolean;
  onAddManufacturerRef?: React.MutableRefObject<(() => void) | undefined>;
  onAddSeriesRef?: React.MutableRefObject<(() => void) | undefined>;
}

const CatalogueContent: React.FC<CatalogueContentProps> = ({ canEdit, onAddManufacturerRef, onAddSeriesRef }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const manufacturers = useAppSelector(getCatalogueManufacturers);
  const selectedManufacturer = useAppSelector(getSelectedManufacturer);
  const selectedSeries = useAppSelector(getSelectedSeries);
  const selectedProduct = useAppSelector(getSelectedProduct);
  const selectedVariantId = useAppSelector(getCatalogueSelectedVariantId);
  const isLoading = useAppSelector(getCatalogueIsLoading);
  const isLoadingProductDetail = useAppSelector(getCatalogueIsLoadingProductDetail);

  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddManufacturerModalOpen, setIsAddManufacturerModalOpen] = useState(false);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isAddSeriesModalOpen, setIsAddSeriesModalOpen] = useState(false);

  useEffect(() => {
    if (onAddManufacturerRef) {
      onAddManufacturerRef.current = () => setIsAddManufacturerModalOpen(true);
    }
  }, [onAddManufacturerRef]);

  useEffect(() => {
    if (onAddSeriesRef) {
      onAddSeriesRef.current = () => setIsAddSeriesModalOpen(true);
    }
  }, [onAddSeriesRef]);

  // Fetch manufacturers on mount.
  // setManufacturers (called by the saga) clears the fetchedIds cache,
  // so the URL-restore flow below will automatically re-fetch any
  // previously selected manufacturer's series/products from the API.
  useEffect(() => {
    dispatch(fetchManufacturers());
  }, [dispatch]);

  // ──────────────────────────────────────────────
  // URL → State: restore selection from query params on initial load.
  // ──────────────────────────────────────────────
  type RestorePhase =
    | 'idle'
    | 'awaiting-manufacturers'
    | 'awaiting-series'
    | 'awaiting-products'
    | 'awaiting-variant-detail'
    | 'done';
  const [restorePhase, setRestorePhase] = useState<RestorePhase>('idle');

  const urlManufacturerId = useRef(searchParams.get('manufacturerId') ?? selectedManufacturer?.id ?? null);
  const urlSeriesId = useRef(searchParams.get('seriesId') ?? selectedSeries?.id ?? null);
  const urlProductId = useRef(searchParams.get('productId') ?? selectedProduct?.id ?? null);
  const urlVariantId = useRef(searchParams.get('variantId') ?? selectedVariantId ?? null);

  useEffect(() => {
    if (urlManufacturerId.current) {
      setRestorePhase('awaiting-manufacturers');
    } else {
      setRestorePhase('done');
    }
  }, []);

  useEffect(() => {
    if (restorePhase !== 'awaiting-manufacturers') return;
    if (isLoading || manufacturers.length === 0) return;

    const targetId = urlManufacturerId.current;
    if (!targetId) {
      setRestorePhase('done');
      return;
    }

    const manufacturer = manufacturers.find((m) => String(m.id) === String(targetId));
    if (manufacturer) {
      dispatch(selectManufacturer(manufacturer.id));
      setRestorePhase(urlSeriesId.current ? 'awaiting-series' : 'done');
    } else {
      setRestorePhase('done');
    }
  }, [restorePhase, manufacturers, isLoading, dispatch]);

  useEffect(() => {
    if (restorePhase !== 'awaiting-series') return;
    if (!selectedManufacturer || selectedManufacturer.series.length === 0) return;

    const targetId = urlSeriesId.current;
    if (!targetId) {
      setRestorePhase('done');
      return;
    }

    const series = selectedManufacturer.series.find((s) => String(s.id) === String(targetId));
    if (series) {
      dispatch(selectSeries(series.id));
      setRestorePhase(urlProductId.current ? 'awaiting-products' : 'done');
    } else {
      setRestorePhase('done');
    }
  }, [restorePhase, selectedManufacturer, dispatch]);

  useEffect(() => {
    if (restorePhase !== 'awaiting-products') return;
    if (!selectedSeries || selectedSeries.products.length === 0) return;

    const targetId = urlProductId.current;
    if (!targetId) {
      setRestorePhase('done');
      return;
    }

    const product = selectedSeries.products.find((p) => String(p.id) === String(targetId));
    if (product) {
      dispatch(selectProduct(product.id));
      setRestorePhase(urlVariantId.current ? 'awaiting-variant-detail' : 'done');
    } else {
      setRestorePhase('done');
    }
  }, [restorePhase, selectedSeries, dispatch]);

  useEffect(() => {
    if (restorePhase !== 'awaiting-variant-detail') return;
    if (!selectedProduct || isLoadingProductDetail) return;

    const targetId = urlVariantId.current;
    if (targetId) {
      const variant = selectedProduct.variants.find((v) => String(v.id) === String(targetId));
      if (variant) {
        dispatch(selectVariant(variant.id));
      }
    }
    setRestorePhase('done');
  }, [restorePhase, selectedProduct, selectedProduct?.variants, isLoadingProductDetail, dispatch]);

  // ──────────────────────────────────────────────
  // State → URL
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (restorePhase !== 'done') return;

    const params = new URLSearchParams();
    if (selectedManufacturer) params.set('manufacturerId', selectedManufacturer.id);
    if (selectedSeries) params.set('seriesId', selectedSeries.id);
    if (selectedProduct) {
      params.set('productId', selectedProduct.id);
      if (selectedVariantId) params.set('variantId', selectedVariantId);
    }

    setSearchParams(params, { replace: true });
  }, [selectedManufacturer, selectedSeries, selectedProduct, selectedVariantId, restorePhase, setSearchParams]);

  const handleAddProduct = (name: string, description?: string, imageBase64?: string) => {
    if (selectedSeries) {
      dispatch(createProduct({ seriesId: selectedSeries.id, name, description, imageBase64 }));
    }
  };

  const handleAddManufacturer = (name: string, logoBase64?: string) => {
    dispatch(createManufacturer({ name, logoBase64 }));
  };

  const handleAddSeries = (name: string, description?: string) => {
    if (selectedManufacturer) {
      dispatch(catalogueReducer.actions.createSeries({ manufacturerId: selectedManufacturer.id, name, description }));
    }
  };

  const handleBack = () => {
    dispatch(selectProduct(null));
    dispatch(selectSeries(null));
    dispatch(selectManufacturer(null));
  };

  const showBackButton = !!(selectedManufacturer || selectedSeries || selectedProduct);

  return (
    <>
      {/* Level 1 - Manufacturer Sidebar */}
      <CatalogueManufacturerSidebar
        onOpenAddManufacturerModal={() => setIsAddManufacturerModalOpen(true)}
        canEdit={canEdit}
      />

      {/* When a manufacturer is selected, show 2-column grid layout */}
      <div className="flex-1 flex flex-col bg-black text-white overflow-hidden border-r border-zinc-800">
        {showBackButton && (
          <div className="p-4 md:hidden">
            <button
              onClick={handleBack}
              className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs border border-gray-600 text-white hover:bg-greyP5 hover:border-gray-500`}
              style={{
                width: 'fit-content',
                height: '36px',
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '18px',
                paddingRight: '18px',
                gap: '8px',
              }}
            >
              <ChevronLeft size={16} color="white" />
              <span>{t('common.back')}</span>
            </button>
          </div>
        )}

        {selectedManufacturer ? (
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
            {/* Left Column - Series/Lines */}
            <div className="flex-shrink-0 flex flex-col overflow-hidden w-full md:w-[229px]">
              <div
                className={`flex-shrink-0 flex items-center px-4 md:px-6 border-b border-zinc-800 md:border-r 
                  ${selectedProduct ? 'md:border-r-black' : 'md:border-r-zinc-800 '}`}
                style={{ height: '45px' }}
              >
                <h2 className="text-lg md:text-xl font-light text-white truncate" title={selectedManufacturer.name}>
                  {selectedManufacturer.name}
                </h2>
              </div>

              <CatalogueSeriesSidebar canEdit={canEdit} />
            </div>

            {/* Right Column - Product Grid, Product Sidebar, or Product Detail */}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              {selectedSeries ? (
                selectedProduct ? (
                  <>
                    <div className="flex-shrink-0 flex flex-col overflow-hidden w-full md:w-[229px]">
                      <div
                        className={`flex-shrink-0 hidden md:flex items-center px-6 border-b border-zinc-800 border-r 
                          ${!selectedProduct ? 'border-r-black' : 'border-r-zinc-800 '}
                          `}
                        style={{ height: '45px' }}
                      ></div>
                      <CatalogueProductSidebar canEdit={canEdit} />
                    </div>
                    <ProductDetail canEdit={canEdit} />
                  </>
                ) : (
                  <ProductGridView
                    products={selectedSeries.products}
                    selectedProductId={null}
                    onProductSelect={(productId) => dispatch(selectProduct(productId))}
                    onAddProduct={canEdit ? () => setIsAddProductModalOpen(true) : undefined}
                    isLoading={isLoading}
                  />
                )
              ) : (
                <div className="flex-1 flex items-center justify-center text-greyP3">
                  {/* Empty state when no series selected */}
                </div>
              )}
            </div>
          </div>
        ) : (
          <ManufacturerGrid />
        )}
      </div>

      {canEdit && (
        <AddManufacturerModal
          isOpen={isAddManufacturerModalOpen}
          onClose={() => setIsAddManufacturerModalOpen(false)}
          onAddManufacturer={handleAddManufacturer}
        />
      )}

      {canEdit && selectedSeries && (
        <AddProductModal
          isOpen={isAddProductModalOpen}
          onClose={() => setIsAddProductModalOpen(false)}
          onAddProduct={handleAddProduct}
          seriesName={selectedSeries.name}
        />
      )}

      {canEdit && selectedManufacturer && (
        <AddSeriesModal
          isOpen={isAddSeriesModalOpen}
          onClose={() => setIsAddSeriesModalOpen(false)}
          onAddSeries={handleAddSeries}
          manufacturerName={selectedManufacturer.name}
        />
      )}
    </>
  );
};

// Main page component
export const CatalogueViewPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const selectedManufacturer = useAppSelector(getSelectedManufacturer);
  const onAddManufacturerRef = useRef<(() => void) | undefined>();
  const onAddSeriesRef = useRef<(() => void) | undefined>();

  const canEdit = user?.roles?.some((role) => CATALOGUE_WRITE_ROLES.includes(role)) ?? false;
  // Admin detection accepts the same shapes used elsewhere (e.g. ProjectsPage):
  // 'ADMIN' / 'ROLE_ADMIN' in roles[], or a top-level user.role string.
  const isAdmin =
    !!user?.roles?.includes('ADMIN') ||
    !!user?.roles?.includes('ROLE_ADMIN') ||
    user?.role === 'ADMIN' ||
    user?.role === 'ROLE_ADMIN';
  // Analytics is restricted to admins or users with the MANUFACTURER role.
  const canAccessAnalytics = isAdmin || (user?.roles?.includes('MANUFACTURER') ?? false);

  const handleTabChange = useDashboardTabNavigation('catalogue');

  if (!user) {
    return null;
  }

  const handleCreateCatalogue = () => {
    if (selectedManufacturer) {
      onAddSeriesRef.current?.();
    } else {
      onAddManufacturerRef.current?.();
    }
  };

  return (
    <div className="family-inter">
      <Header
        activeTab="catalogue"
        onTabChange={handleTabChange}
        showAnalyticsTab={canAccessAnalytics}
        onCreateCatalogue={canEdit ? handleCreateCatalogue : undefined}
        isCatalogueSelected={!!selectedManufacturer}
      />

      <div className="relative flex overflow-hidden" style={{ height: 'calc(100vh - 226px)' }}>
        <CatalogueContent
          canEdit={canEdit}
          onAddManufacturerRef={onAddManufacturerRef}
          onAddSeriesRef={onAddSeriesRef}
        />

        {isAdmin && (
          <button
            type="button"
            onClick={() => dispatch(syncCatalogue())}
            title={t('catalogue.syncTooltip')}
            className="absolute top-3 right-4 z-10 flex items-center gap-2 px-4 rounded-full font-medium
              transition-colors duration-200 text-xs border
              border-gray-600 text-white hover:bg-greyP5 hover:border-gray-500 bg-black/40 backdrop-blur-sm"
            style={{ height: '36px', paddingTop: '10px', paddingBottom: '10px' }}
          >
            <RefreshCw size={14} />
            <span>{t('catalogue.sync')}</span>
          </button>
        )}
      </div>

      <Footer projectPage={true} activeTab="catalogue" />
    </div>
  );
};
